import { LaskunListaustietorivi, LaskunListaustyyppi, LaskunTila } from '@lemon/core-firebase/model/lasku'
import { LocalDate, LocalMonth, NumberDate, NumberMonth } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

export interface LaskulistanYhteensaRivi {
  valuutta: string
  avoinna: number
  laskutettu: number
}

export interface KuukaudenPaivarajat {
  alkaen: NumberDate
  ennen: NumberDate
}

const RAJATTAVIA_KUUKAUSIA = 5

function onkoPaarivi(rivi: Pick<LaskunListaustietorivi, 'tyyppi'>): boolean {
  return rivi.tyyppi === LaskunListaustyyppi.TAVALLINEN || rivi.tyyppi === LaskunListaustyyppi.VANHEMPI
}

/** Kuukausirajauksen vaihtoehdot: kuluva kuukausi ja sitä edeltävät, uusin ensin. */
export function annaRajattavatKuukaudet(kuluvaKuukausi: LocalMonth, dateService: DateService): LocalMonth[] {
  const kuukaudet: LocalMonth[] = []
  let kuukausi = kuluvaKuukausi
  while (kuukaudet.length < RAJATTAVIA_KUUKAUSIA) {
    kuukaudet.push(kuukausi)
    kuukausi = dateService.lisaaKuukausiaLocalMonth(kuukausi, -1)
  }
  return kuukaudet
}

export function annaKuukaudenPaivarajat(kuukausi: NumberMonth, dateService: DateService): KuukaudenPaivarajat {
  return { alkaen: kuukausi * 100 + 1, ennen: dateService.lisaaKuukausiaKuukaudenNumero(kuukausi, 1) * 100 + 1 }
}

/** Myöhästyminen näytetään vain erääntyneen laskun pääriville, ei sen muistutuksille, hyvityksille tai perintämerkinnöille. */
export function annaPaiviaMyohassa(rivi: Pick<LaskunListaustietorivi, 'tyyppi' | 'tila' | 'erapvm'>, tanaan: LocalDate, dateService: DateService): number | null {
  if (!onkoPaarivi(rivi) || rivi.tila !== LaskunTila.eraantynyt || !rivi.erapvm) {
    return null
  }
  return dateService.paiviaValissaPaikallinen(tanaan, rivi.erapvm)
}

/**
 * Näkyvien rivien summat valuutoittain. Muun valuutan lasku, jolle on tallennettu kurssi, lasketaan euroihin; ilman
 * kurssia se jää omaksi valuutakseen. Avoimeen summaan lasketaan vain lähetettyjen laskujen päärivit, koska lapsirivit
 * toistavat ryppään avoimen summan. Perintämerkinnöillä ei ole valuuttaa eikä summaa.
 */
export function laskeLaskulistanYhteensaRivit(rivit: LaskunListaustietorivi[], annaEurokurssi: (rivi: LaskunListaustietorivi) => number | undefined, currencyService: CurrencyService): LaskulistanYhteensaRivi[] {
  const valuutoittain = new Map<string, LaskulistanYhteensaRivi>()
  for (const rivi of rivit) {
    if (!rivi.valuutta) {
      continue
    }
    const kurssi = annaEurokurssi(rivi)
    const valuutta = kurssi ? 'EUR' : rivi.valuutta
    const yhteensa = valuutoittain.get(valuutta) ?? { valuutta, avoinna: 0, laskutettu: 0 }
    valuutoittain.set(valuutta, yhteensa)
    if (rivi.tila !== LaskunTila.luonnos && onkoPaarivi(rivi)) {
      yhteensa.avoinna += annaSummaEuroinaTaiAlkuperainen(rivi.avoinna, kurssi, currencyService)
    }
    yhteensa.laskutettu += annaSummaEuroinaTaiAlkuperainen(rivi.summa, kurssi, currencyService)
  }
  return [...valuutoittain.values()]
    .map(yhteensa => ({ ...yhteensa, avoinna: currencyService.roundHalfUp(yhteensa.avoinna, 2), laskutettu: currencyService.roundHalfUp(yhteensa.laskutettu, 2) }))
    .sort((a, b) => a.laskutettu - b.laskutettu)
}

function annaSummaEuroinaTaiAlkuperainen(summa: number | null, kurssi: number | undefined, currencyService: CurrencyService): number {
  if (!summa) {
    return 0
  }
  return kurssi ? currencyService.roundHalfUp(summa * kurssi, 2) : summa
}
